/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import BookingPackageItemsWithKarting from "pages/Front/Booking/BookingPackageItems/BookingPackageItemsWithKarting";
import BookingPackageItemsDurations from "pages/Front/Booking/BookingPackageItems/BookingPackageItemsDurations";
import {currencyNumberFormat} from "utils/utility";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {PackagesItemsPerson} from "types/entities/PackagesItems";
import {useTranslation} from "react-i18next";
import {usePackageItemKartingOptions} from "utils/packageUtils";
import {UserIcon} from "@heroicons/react/24/solid";
import clsx from "clsx";
import {useTypedSelector} from "helpers/reducers";
import {PackageCategory} from "types/enums/PackageCategory";
import Dates from "utils/Dates";
import {AVAILABLE_DURATIONS_BASIC} from "helpers/config";

interface BookingPackagesNewDriverRowProps {
    index: number;
    i: number;
    personType: PackageItemPersonType;
}

const BookingPackagesNewDriverRow = (props: BookingPackagesNewDriverRowProps) => {
    const {t} = useTranslation();
    const availablePackageItems = useTypedSelector(state => state.bookingState.bookingProcess.availablePackageItems);
    const selectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);
    const bookingProcess = useTypedSelector(state => state.bookingState.bookingProcess);
    const settings = useTypedSelector(state => state.settingsState);
    const [isKartingDisabled, setIsKartingDisabled] = useState<boolean>(!!settings?.config?.disable_karting_tickets || false);
    const [withKarting, setWithKarting] = useState<number | undefined>(isKartingDisabled ? 0 : 1);
    const [chosenDuration, setChosenDuration] = useState<number | undefined>(240);
    const [selectedPackageItem, setSelectedPackageItem] = useState<PackagesItemsPerson | null>(null);
    const {getPackageItemForCombination, setPackageItemHandler} = usePackageItemKartingOptions();
    const [kartingIsNotAvailable, setKartingIsNotAvailable] = useState<boolean>(false);

    const isAnyPackageItemAvailableWithKarting = () => {
        for (const duration of AVAILABLE_DURATIONS_BASIC) {
            const packageItem = getPackageItemForCombination(duration, true, props.personType);
            if (packageItem) {
                if (duration !== 60 || (duration === 60 && Dates.hour(selectedTimeslot?.start_at) >= 18)) {
                    return true;
                }
            }
        }
        return false;
    }

    const findClosestPackageItemDuration = (newDuration: number | undefined, newKartingState: number) => {
        let availableDurations = AVAILABLE_DURATIONS_BASIC;
        if (newDuration !== undefined && availableDurations.includes(newDuration)) {
            availableDurations = [
                newDuration,
                ...availableDurations.filter((duration) => duration !== newDuration),
            ];
        }

        for (const duration of availableDurations) {
            const packageItem = getPackageItemForCombination(duration, newKartingState == 1, props.personType);
            if (packageItem) {
                if (duration !== 60 || (duration === 60 && Dates.hour(selectedTimeslot?.start_at) >= 18)) {
                    return duration;
                }
            }
        }

        return undefined;
    }

    const setPackageItem = (newDuration: number | undefined, newKartingState: number | undefined) => {
        const isKartingAvailable = isAnyPackageItemAvailableWithKarting();
        setKartingIsNotAvailable(!isKartingAvailable);
        const currentIsKarting = isKartingDisabled
            ? 0
            : isKartingAvailable && newKartingState == 1 ? 1 : 0;

        const packageItemDuration = findClosestPackageItemDuration(newDuration, currentIsKarting);
        if (packageItemDuration !== undefined) {
            setWithKarting(currentIsKarting);
            setChosenDuration(packageItemDuration);
            packageItemChangeHandler(packageItemDuration, currentIsKarting);
        }
    }

    const packageItemChangeHandler = (newChosenDuration: number | undefined, newKartingState: number | undefined) => {
        if (newChosenDuration && newKartingState !== undefined) {
            const packageItem = getPackageItemForCombination(newChosenDuration, newKartingState == 1, props.personType);
            if (packageItem) {
                setSelectedPackageItem(packageItem);
                setPackageItemHandler(props.index, packageItem.id, props.personType, 1);
            } else {
                setPackageItemHandler(props.index, null, props.personType, 1);
            }
        }
    };

    useEffect(() => {
        if (availablePackageItems) {
            setPackageItem(chosenDuration, withKarting);
        }
    }, [withKarting, chosenDuration, availablePackageItems]);

    useEffect(() => {
        setIsKartingDisabled(!!settings?.config?.disable_karting_tickets || false);
    }, [settings?.config?.disable_karting_tickets]);

    return (
        <tr
            className={
                "BookingPackagesNewDriverRow flex flex-col lg:table-row last-of-type:border-b-0 border-b lg:border-b-0 px-2 pt-3 pb-2 sm:w-auto sm:px-4 sm:py-4 lg:px-0 lg:py-0"
            }
        >
            <td
                className={
                    "align-middle py-2 lg:pr-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell border-b border-dashed sm:border-b-0"
                }
            >
                <div className={"flex flex-row items-center justify-start mb-2 sm:mb-0"}>
                    <UserIcon
                        className={clsx(
                            "h-7 w-7 xl:h-8 xl:w-8 flex-none mr-2",
                            props.personType === PackageItemPersonType.Adult ? "text-teal-500" : "text-pink-400"
                        )}
                    />
                    <div className={"flex flex-col items-center"}>
                        <span className={"font-bold text-sm xl:text-base"}>
                            {props.i + 1}. {t("person")}
                        </span>
                        {bookingProcess.filter &&
                            (bookingProcess.filter.category === PackageCategory.Action || bookingProcess.filter.category === PackageCategory.School|| bookingProcess.filter.category === PackageCategory.FerienPass) && (
                                <span className={"text-xs text-gray-500 font-medium text-center"}>{t(props.personType)}</span>
                            )}
                    </div>
                </div>
            </td>
            <td className={"align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell"}>
                <h4 className={"lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]"}>
                    {t("Options")}
                    <span>:</span>
                </h4>
                <BookingPackageItemsWithKarting className={"justify-start lg:justify-center"} kartingIsNotAvailable={kartingIsNotAvailable} setWithKarting={setWithKarting} withKarting={withKarting} />
            </td>
            <td className={"align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell"}>
                <h4 className={"lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]"}>
                    {t("Ticket by hours")}
                    <span>:</span>
                </h4>
                <BookingPackageItemsDurations
                    disabled1Hour={Dates.hour(selectedTimeslot?.start_at) < 18}
                    disabled2Hours={false}
                    chosenDuration={chosenDuration}
                    className={"justify-start lg:justify-center"}
                    enabled={withKarting !== undefined}
                    personType={props.personType}
                    setChosenDuration={setChosenDuration}
                    withKarting={withKarting}
                />
            </td>
            <td className={"align-middle py-2 lg:pl-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell"}>
                <h4 className={"lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]"}>
                    {t("Price")}
                    <span>:</span>
                </h4>
                <div
                    className={clsx(
                        "flex flex-row items-center justify-center font-medium text-gray-700 text-sm xl:text-base",
                        selectedPackageItem?.price && "underline underline-offset-4 lg:underline-offset-8"
                    )}
                >
                    {selectedPackageItem?.price ? <span>{currencyNumberFormat(selectedPackageItem.price)}</span> : <span>---</span>}
                </div>
            </td>
        </tr>
    );
};

export default BookingPackagesNewDriverRow;
